export class SheetController {
    /**
     * Constructs a new instance of the SheetController class.
     * 
     * @param {Object} $scope - The AngularJS scope object.
     * @param {Object} $routeParams - The route parameters.
     * @param {Object} $location - The AngularJS $location service.
     * @param {Object} $signals - The signals service for event handling.
     * @param {Object} $bridges - The bridges service for various utilities.
     * @param {Object} TaskSelectionService - The service for task selection.
     * @param {Object} TaskDataService - The service for task data.
     * @param {Object} TaskFlowService - The service for task flow management.
     * @param {Object} UserInfoService - The service for user information.
     * @param {Object} RepositoryEntry - The repository entry service.
     * @param {Object} $rootScope - The AngularJS root scope.
     * @param {Object} VersioningAndLockingService - The service for versioning and locking.
     * @param {Object} $uibModal - The AngularUI Bootstrap modal service.
     * @param {Object} TaskCountdownService - The service for task countdown.
     * @param {Object} TaskInfoService - The service for task information.
     * @param {Object} SheetLoadedService - The service for sheet loading.
     * @param {Object} $config - The configuration service.
     * @param {Object} $route - The AngularJS $route service.
     * @param {Object} dialogs - The dialogs service.
     * @param {Object} $translate - The translation service.
     */
    constructor($scope, $routeParams, $location, $signals, $bridges, TaskSelectionService, TaskDataService, TaskFlowService, UserInfoService, RepositoryEntry, $rootScope, VersioningAndLockingService, $uibModal, TaskCountdownService, TaskInfoService, SheetLoadedService, $config, $route, dialogs, $translate) {
        // Initialize the promise for sheet loading
        SheetLoadedService.initPromise();

        // Define a function to get the promise for sheet loading
        $scope.getPromise = function(){
            if(!isCMS && !$config.isPDFMode()){
                return SheetLoadedService.getPromise();
            }
        };

        // Ensure that all necessary redirects for task flow are registered
        TaskFlowService.initRedirects($scope, $location);
        var taskInfo = TaskInfoService.getTaskInfo();

        // Make sure the "scroll down to continue" ribbon is displayed
        activateScrollDownToContinue();

        // Controller meta setup
        $scope._logIdentifier = "SheetController";
        var $logger         = $bridges.logger(),
            $auth           = $bridges.authentication();

        // Private scope variables
        var candidateUUID = $auth.getUserUUID(),
            candidate     = UserInfoService.getUserInfo(candidateUUID),
            taskContext   = TaskDataService.fetchContext();

        // Determine the current task's UUID
        var taskUUID      = TaskSelectionService.getTaskUUID(); // In the frontend, this will be in the candidate details (more or less the "session")

        // Read Task & Sheet Model from Repository
        var task          = taskContext.read(taskUUID),
            sheet         = taskContext.read($routeParams.sheetUUID),
            countDown = TaskCountdownService.getInstance();

        $rootScope.isJournal = sheet.isJournal();        

        $scope.writingDirection = $rootScope.isJournal ? "ltr" : taskInfo.getInputLanguage().writingDirection;

        if (!isCMS) {
            if (countDown.getMode() === 'journal' && !sheet.isJournal()) {
                $location.path('/sheet/' + task.getJournal().getUUID());
                return false;
            }
            if (countDown.getMode() !== 'journal' && sheet.isJournal()) {
                $location.path('/overview/' + task.getUUID());
            }
        }

        // Handle pause
        if (countDown.isPaused()) {
            $location.path('/pause/' + $routeParams.sheetUUID);
            return;
        }

        if(angular.isFunction(task.isSheet) && task.isSheet()){
            TaskSelectionService.detectTaskUUIDFromUserInfo();
            taskUUID      = TaskSelectionService.getTaskUUID();
            task          = taskContext.read(taskUUID);
        }
        // Controller attributes
        $scope.task               = task;
        $scope.candidate          = candidate;
        $scope.sheet              = sheet;
        $scope.layout             = sheet.getLayout();
        $scope.rows               = $scope.layout.getRows();
        $scope.waitRequests       = [];
        $scope.closedWaitRequests = [];
        $scope.sheetsLength       = task.getSheets().length;

        // Activate preview+createSheet+save functions on navigation bar
        $scope.navBarBtnPreviewAvailable        = true;
        $scope.navBarBtnCreateSheetAvailable    = true;
        $scope.navBarBtnSaveAvailable           = true;
        $scope.navBarBtnEditIntroductionPageAvailable = true;
        $scope.navBarBtnIFrameToolAvailable     = true;

        // Should the sheet scroll to a fixed QuestionPart UUID?
        if(isCMS){
            if ($routeParams.scrollToUUID) {
                $scope.scrollTargetUUID = $routeParams.scrollToUUID;
            } else if ($routeParams.preview && angular.isUUID($routeParams.preview)) {
                $scope.scrollTargetUUID = $routeParams.preview;
            }
        }else {
            $scope.scrollTargetUUID   = $routeParams.scrollToUUID;
        }
        $scope.shouldScrollTo     = $routeParams.scrollToUUID !== undefined;

        $scope.safeApply = this.safeApply.bind(this);
        $scope.initBindings = this.initBindings.bind(this);
        $scope.isSheetProcessing = this.isSheetProcessing.bind(this);
        $scope.handleSheetWaitRequest = this.handleSheetWaitRequest.bind(this);
        $scope.handleSheetWaitRequestClosed = this.handleSheetWaitRequestClosed.bind(this);
        $scope.handleAutoSave = this.handleAutoSave.bind(this);
        $scope.handleModelChange = this.handleModelChange.bind(this);
        $scope.handleLocationChangeStart = this.handleLocationChangeStart.bind(this);
        $scope.$on('$routeChangeStart', (e, next, current) => {
            if (!this.TaskFlowService.isRouteChangeAllowedDuringExam(e, next, current)) {
                console.log('SheetController routeChangeStart -> disallowed', e, next, current);
                return;
            }
            this.handleRouteChangeStart(e, next, current);
        });
        $scope.onHomeButtonCallback = this.onHomeButtonCallback.bind(this);

        if(isCMS){
            // ------------------------------------------------------------------------------------------------------
            // Versioning
            var versioningData = VersioningAndLockingService.getData();
            $scope.isLocked = $rootScope.isPreview ? false : versioningData.locked;

            $signals.on('versioningDataUpdated', function() {
                versioningData = VersioningAndLockingService.getData();
                $scope.isLocked = versioningData.locked;
            });
        }


        this.$bridges = $bridges;
        this.$scope = $scope;
        this.$rootScope = $rootScope;
        this.$signals = $signals;
        this.$location = $location;
        this.$logger = $logger;
        this.TaskFlowService = TaskFlowService;

        $scope.initBindings();
 
    }

    /**
     * Safely applies a function to the AngularJS digest cycle.
     * 
     * @param {Function} fn - The function to apply.
     */
    safeApply(fn) {
        var phase = this.$rootScope.$$phase;
        if(phase == '$apply' || phase == '$digest') {
            if(fn && (typeof(fn) === 'function')) {
                fn();
            }
        } else {
            this.$scope.$apply(fn);
        }
    }

    /**
     * Initializes the bindings for the controller.
     */
    initBindings() {
        var $logger = this.$bridges.logger();
        $logger.dev(this.$scope._logIdentifier,"initBindings");

        var SIGNALS,
            signalEvents,
            scopeEventDeregisterFns,
            key;

        // Configure $signal events
        SIGNALS      = IB.Signals;
        signalEvents = {};
        scopeEventDeregisterFns = [];

        signalEvents[IB.Signals.SHEET_WAIT_REQUEST]        = this.$scope.handleSheetWaitRequest;
        signalEvents[IB.Signals.SHEET_WAIT_REQUEST_CLOSED] = this.$scope.handleSheetWaitRequestClosed;
        signalEvents[IB.Signals.MODELCHANGE]               = this.$scope.handleModelChange;
        signalEvents[IB.Signals.AUTO_SAVE]                 = this.$scope.handleAutoSave;
        signalEvents['$locationChangeStart']               = this.$scope.handleLocationChangeStart;
        signalEvents['$routeChangeStart']                  = this.$scope.handleRouteChangeStart;

        // Bind $signal events
        for(key in signalEvents) {
            if(key.indexOf('$') > -1) {
                scopeEventDeregisterFns.push(this.$scope.$on(key, signalEvents[key]));
            } else {
                this.$signals.on(key, signalEvents[key]);
            }
        }

        // Clean up $signal events when scope has been left
        this.$scope.$on('$destroy', function() {
            var key;

            $logger.dev(this.$scope._logIdentifier,"initBindings->$destroy");

            for(key in signalEvents) {
                if(key.indexOf('$') === -1) {
                    this.$signals.remove(key, signalEvents[key]);
                }
            }

            scopeEventDeregisterFns.forEach(function(deregister) {
                if(typeof deregister === 'function') {
                    deregister();
                }
            });
        }.bind(this));
    }

    /**
     * Checks if the sheet is currently processing.
     * 
     * @returns {boolean} - True if the sheet is processing, false otherwise.
     */
    isSheetProcessing() {
        var isProcessing,
            hasOpenWaitRequests,
            isInSavingMode;

        hasOpenWaitRequests = this.$scope.closedWaitRequests.length < this.$scope.waitRequests.length;
        isInSavingMode      = false;
        isProcessing        = hasOpenWaitRequests || isInSavingMode;

        return isProcessing;
    }

    /**
     * Handles a sheet wait request.
     * 
     * @param {string} uuid - The UUID of the request.
     * @param {string} objectType - The type of the object.
     */
    handleSheetWaitRequest(uuid, objectType) {
        this.$scope.waitRequests = this.$scope.waitRequests || [];
        this.$scope.waitRequests.push(uuid);
    }

    /**
     * Handles a closed sheet wait request.
     * 
     * @param {string} uuid - The UUID of the request.
     * @param {string} objectType - The type of the object.
     */
    handleSheetWaitRequestClosed(uuid, objectType) {
        this.$scope.closedWaitRequests = this.$scope.closedWaitRequests || [];
        this.$scope.closedWaitRequests.push(uuid);
    }

    /**
     * Handles the auto-save functionality.
     * 
     * @param {Object} evnt - The event object.
     */
    handleAutoSave(evnt) {
        var $logger,
            isReadyForSaving,
            questionParts, idx;

        $logger = this.$bridges.logger();
        isReadyForSaving = !this.$scope.isSheetProcessing();

        if(isReadyForSaving) {
            $logger.dev(this.$scope._logIdentifier,"handleAutoSave->saving");
            questionParts = this.$scope.sheet.getQuestionParts();
            for(idx in questionParts) {
                questionParts[idx].save();
            }
        } else {
            $logger.dev(this.$scope._logIdentifier,"handleAutoSave->not saving, sheet is processing");
        }
    }

    /**
     * Handles model changes.
     * 
     * @param {string} uuid - The UUID of the model.
     */
    handleModelChange(uuid) {
        this.$scope.safeApply();
    }

    /**
     * Handles the start of a location change.
     * 
     * @param {Object} evnt - The event object.
     * @param {string} newUrl - The new URL.
     * @param {string} oldUrl - The old URL.
     */
    handleLocationChangeStart(evnt, newUrl, oldUrl){
        var shallStopRouteChange;

        shallStopRouteChange = this.$scope.isSheetProcessing();

        if(shallStopRouteChange) {
            evnt.preventDefault();
        }
    }

    /**
     * Handles the start of a route change.
     * 
     * @param {Object} next - The next route.
     * @param {Object} current - The current route.
     */
    handleRouteChangeStart(next, current) {
        this.$signals.emit(IB.Signals.BEFORE_SHEET_BLUR);
    }

    /**
     * Handles the home button click callback.
     * 
     * @param {Object} task - The task object.
     */
    onHomeButtonCallback(task) {
        var $logger = this.$bridges.logger();
        $logger.logEnter(this.$scope, 'onHomeButtonClick', task);
        if(isCMS && this.$config.isPreviewMode()){
            this.$location.path('/overview/' + task.getUUID()+"/preview");
        }else{
            this.$location.path('/overview/' + task.getUUID());
        }
    }
}

export default function registerSheetController(assessmentFrontendControllers) {
    assessmentFrontendControllers.controller('SheetController', [
        '$scope',
        '$routeParams',
        '$location',
        '$signals',
        '$bridges',
        'TaskSelectionService',
        'TaskDataService',
        'TaskFlowService',
        'UserInfoService',
        'RepositoryEntry',
        '$rootScope',
        'VersioningAndLockingService',
        '$uibModal',
        'TaskCountdownService',
        'TaskInfoService',
        'SheetLoadedService',
        '$config',
        '$route',
        'dialogs',
        '$translate',
        SheetController
    ]);
}
